import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  MessagesSquare,
  Activity,
  Contact as ContactIcon,
  Bot,
  Radio,
  Flame,
  Wrench,
  AlertTriangle,
  Coins,
  type LucideIcon,
} from 'lucide-react';
import * as conversationsApi from '@/api/conversations.api';
import * as contactsApi from '@/api/contacts.api';
import * as agentsApi from '@/api/agents.api';
import * as channelsApi from '@/api/channels.api';
import * as dashboardApi from '@/api/dashboard.api';
import * as ventasApi from '@/api/ventas.api';
import * as installationApi from '@/api/installation.api';
import * as consumosApi from '@/api/consumos.api';
import type { VentasPeriodo } from '@/api/ventas.api';
import { useAuthStore } from '@/store/auth.store';
import { useVeRuta } from '@/hooks/useMenu';
import { hasRole } from '@conversia/shared-types';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { relativeTime, dateTime } from '@/lib/format';
import { statusBadge } from '@/lib/badges';
import { money } from '@/lib/money';
import { cn } from '@/lib/cn';

/**
 * DASHBOARD. Para el ADMIN es el centro del negocio: cómo va el día (calientes
 * sin atender, trabajos de hoy, quejas) + las ventas del período, todo
 * clickeable hacia la vista que resuelve. Para el resto de usuarios queda el
 * resumen genérico de siempre (los endpoints del negocio son admin-only).
 */

function MetricCard({
  icon: Icon,
  label,
  value,
  loading,
  accent,
}: {
  icon: LucideIcon;
  label: string;
  value?: number;
  loading: boolean;
  accent: string;
}) {
  return (
    <Card>
      <CardBody className="flex items-center gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
          {loading ? (
            <Spinner size={18} className="mt-1" />
          ) : (
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value ?? 0}</p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role ? hasRole(user.role, 'ADMIN') : false;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={`Hola, ${user?.name ?? user?.email?.split('@')[0] ?? ''} 👋`}
        helpKey="dashboard"
        description={
          isAdmin
            ? 'Así va tu negocio hoy. Toca una tarjeta para ir al detalle.'
            : 'Resumen general de tus conversaciones y contactos.'
        }
      />
      {isAdmin ? <BusinessDashboard /> : <GenericDashboard />}
    </div>
  );
}

// ------------------------- Centro del negocio (ADMIN) -------------------------

const PERIODO_CHIPS: { value: VentasPeriodo; label: string }[] = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mes' },
];

function BusinessDashboard() {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState<VentasPeriodo>('mes');
  // El gasto en USD es superficie reservada del OWNER (F2): a un ADMIN la API
  // le responde 403, así que ni se consulta — en su lugar ve el MEDIDOR (F1).
  // 7-oct: lo decide el servidor. El gasto es de quien ve «Gasto de IA» (la administración
  // del sistema), no de todo OWNER.
  const isOwner = useVeRuta('/consumos');

  const resumen = useQuery({
    queryKey: ['dashboard', 'resumen'],
    queryFn: dashboardApi.getDashboardResumen,
  });
  const ventas = useQuery({
    queryKey: ['ventas', 'resumen', periodo],
    queryFn: () => ventasApi.getVentasResumen(periodo),
  });
  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
  });
  // Gasto del mes en IA (tanda sala-maquinas) → tarjeta clickeable a /consumos.
  const consumo = useQuery({
    queryKey: ['consumos', 'summary', 'mes'],
    queryFn: () => consumosApi.getSummary('mes'),
    enabled: isOwner,
  });
  // Medidor del cliente: conversaciones del mes contra el cupo (informativo).
  const meter = useQuery({
    queryKey: ['consumos', 'meter'],
    queryFn: consumosApi.getMeter,
    enabled: !isOwner,
  });
  const currencyPrefix = crmView?.currencyPrefix || 'RD$';

  const calientes = resumen.data?.calientesSinAtender ?? 0;
  const hoy = resumen.data?.trabajosHoy;
  const quejas = resumen.data?.quejas ?? [];

  // Click en "calientes": abre Conversaciones ya filtrado en la pestaña CALIENTE.
  function irACalientes() {
    navigate('/conversations?bandeja=CALIENTE');
  }

  const desgloseHoy = hoy
    ? [
        hoy.asignados ? `${hoy.asignados} por salir` : null,
        hoy.enSitio ? `${hoy.enSitio} en sitio` : null,
        hoy.completados ? `${hoy.completados} completado${hoy.completados === 1 ? '' : 's'}` : null,
        hoy.noCompletados ? `${hoy.noCompletados} no completado${hoy.noCompletados === 1 ? '' : 's'}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <DayCard
          icon={Flame}
          accent={
            calientes > 0
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
          }
          label="Clientes calientes sin atender"
          value={calientes}
          hint={calientes > 0 ? 'Toca para atenderlos ahora' : 'Todo atendido'}
          loading={resumen.isLoading}
          onClick={irACalientes}
        />
        <DayCard
          icon={Wrench}
          accent="bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
          label="Trabajos de hoy"
          value={hoy?.total ?? 0}
          hint={desgloseHoy || 'Sin citas para hoy'}
          loading={resumen.isLoading}
          onClick={() => navigate('/despachos')}
        />
        <DayCard
          icon={AlertTriangle}
          accent={
            quejas.length > 0
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
          }
          label="Quejas de encuesta (últimos 7 días)"
          value={quejas.length}
          hint={quejas.length > 0 ? 'Los detalles están aquí abajo' : 'Ningún cliente se quejó'}
          loading={resumen.isLoading}
        />
        {isOwner ? (
          <DayCard
            icon={Coins}
            accent="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
            label="Este mes en IA (US$)"
            value={Math.round(consumo.data?.total.usd ?? 0)}
            hint={
              consumo.data
                ? `US$${consumo.data.total.usd.toFixed(2)} estimados · toca para el detalle`
                : 'Calculando…'
            }
            loading={consumo.isLoading}
            onClick={() => navigate('/consumos')}
          />
        ) : (
          <DayCard
            icon={Coins}
            accent={
              meter.data?.excedido
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            }
            label="Conversaciones del mes"
            value={meter.data?.conversaciones ?? 0}
            hint={
              meter.data
                ? `de ${meter.data.cupo} del plan (${meter.data.pct}%)${meter.data.excedido ? ' · plan superado — el servicio sigue activo' : ''}`
                : 'Calculando…'
            }
            loading={meter.isLoading}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Ventas del período */}
        <Card className="lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-surface-border p-5 dark:border-slate-800">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Ventas</h2>
            <div className="flex items-center gap-1 rounded-md border border-surface-border p-1 dark:border-slate-700">
              {PERIODO_CHIPS.map((chip) => (
                <button
                  key={chip.value}
                  onClick={() => setPeriodo(chip.value)}
                  className={cn(
                    'rounded px-3 py-1 text-xs font-medium transition-colors',
                    periodo === chip.value
                      ? 'bg-brand-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                  )}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
          <CardBody>
            {ventas.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : !ventas.data ? null : (
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Total vendido</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                      {money(ventas.data.totalVendido, currencyPrefix)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {ventas.data.ventas} venta{ventas.data.ventas === 1 ? '' : 's'} cerrada
                      {ventas.data.ventas === 1 ? '' : 's'}
                      {ventas.data.ticketPromedio != null
                        ? ` · ${money(Math.round(ventas.data.ticketPromedio), currencyPrefix)} por venta`
                        : ''}
                    </p>
                    {ventas.data.sinPrecio > 0 && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        {ventas.data.sinPrecio} venta{ventas.data.sinPrecio === 1 ? '' : 's'} sin
                        monto en la ficha (no suman al total)
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Leads que entraron vs. ventas
                    </p>
                    <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {ventas.data.leadsEntrados} leads →{' '}
                      {ventas.data.conversion != null
                        ? `${Math.round(ventas.data.conversion * 100)}%`
                        : '—'}
                    </p>
                    <p className="text-xs text-slate-400">Indicador orientativo del período</p>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    Lo que más se vende
                  </p>
                  {ventas.data.topModelos.length === 0 ? (
                    <p className="text-xs text-slate-400">
                      Sin ventas cerradas en este período todavía.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {ventas.data.topModelos.map((m) => (
                        <li key={m.modelo} className="flex items-center justify-between gap-3">
                          <span className="truncate text-sm text-slate-700 dark:text-slate-200">
                            {m.modelo}
                          </span>
                          <span className="shrink-0 text-xs text-slate-400">
                            {m.cantidad} × · {money(m.total, currencyPrefix)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Quejas recientes */}
        <Card>
          <div className="border-b border-surface-border p-5 dark:border-slate-800">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Quejas recientes
            </h2>
          </div>
          <CardBody className="p-0">
            {resumen.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : quejas.length === 0 ? (
              <EmptyState
                title="Sin quejas"
                description="Ningún cliente se quejó en la encuesta esta semana."
                className="m-5"
              />
            ) : (
              <ul className="divide-y divide-surface-border dark:divide-slate-800">
                {quejas.map((q) => {
                  const body = (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                          {q.cliente}
                        </p>
                        <span className="shrink-0 text-xs text-slate-400">{dateTime(q.fecha)}</span>
                      </div>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                        “{q.respuesta}”
                      </p>
                    </>
                  );
                  return (
                    <li key={q.jobId}>
                      {q.conversationId ? (
                        <Link
                          to={`/conversations/${q.conversationId}`}
                          className="block px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        >
                          {body}
                        </Link>
                      ) : (
                        <div className="px-5 py-3">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

/** Tarjeta grande del día: número + pista, clickeable hacia su vista. */
function DayCard({
  icon: Icon,
  accent,
  label,
  value,
  hint,
  loading,
  onClick,
}: {
  icon: LucideIcon;
  accent: string;
  label: string;
  value: number;
  hint: string;
  loading: boolean;
  onClick?: () => void;
}) {
  const content = (
    <CardBody className="flex items-center gap-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${accent}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
        {loading ? (
          <Spinner size={18} className="mt-1" />
        ) : (
          <>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</p>
            <p className="truncate text-xs text-slate-400">{hint}</p>
          </>
        )}
      </div>
    </CardBody>
  );
  if (!onClick) return <Card>{content}</Card>;
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      {content}
    </Card>
  );
}

// ------------------------- Resumen genérico (no-admin) -------------------------

function GenericDashboard() {
  const totalConvos = useQuery({
    queryKey: ['conversations', 'count', 'all'],
    queryFn: () => conversationsApi.listConversations({ take: 1 }),
  });
  const activeConvos = useQuery({
    queryKey: ['conversations', 'count', 'active'],
    queryFn: () => conversationsApi.listConversations({ status: 'ACTIVE', take: 1 }),
  });
  const contacts = useQuery({
    queryKey: ['contacts', 'count'],
    queryFn: () => contactsApi.listContacts({ take: 1 }),
  });
  const agents = useQuery({
    queryKey: ['agents', 'count'],
    queryFn: () => agentsApi.listAgents({ take: 1 }),
  });
  const channels = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });
  const recent = useQuery({
    queryKey: ['conversations', 'recent'],
    queryFn: () => conversationsApi.listConversations({ take: 10 }),
  });

  const connectedChannels = channels.data?.filter((c) => c.status === 'CONNECTED').length;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          icon={MessagesSquare}
          label="Conversaciones"
          value={totalConvos.data?.total}
          loading={totalConvos.isLoading}
          accent="bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
        />
        <MetricCard
          icon={Activity}
          label="Activas"
          value={activeConvos.data?.total}
          loading={activeConvos.isLoading}
          accent="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
        />
        <MetricCard
          icon={ContactIcon}
          label="Contactos"
          value={contacts.data?.total}
          loading={contacts.isLoading}
          accent="bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
        />
        <MetricCard
          icon={Bot}
          label="Agentes AI"
          value={agents.data?.total}
          loading={agents.isLoading}
          accent="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        />
        <MetricCard
          icon={Radio}
          label="Canales conectados"
          value={connectedChannels}
          loading={channels.isLoading}
          accent="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
        />
      </div>

      <Card>
        <div className="flex items-center justify-between border-b border-surface-border p-5 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Actividad reciente
          </h2>
          <Link to="/conversations" className="text-sm font-medium text-brand-600 hover:text-brand-700">
            Ver todas
          </Link>
        </div>
        <CardBody className="p-0">
          {recent.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : !recent.data?.items.length ? (
            <EmptyState
              title="Sin actividad todavía"
              description="Cuando lleguen mensajes a tus canales, aparecerán aquí."
              className="m-5"
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {recent.data.items.map((c) => {
                const badge = statusBadge(c.status);
                return (
                  <li key={c.id}>
                    <Link
                      to={`/conversations/${c.id}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <Avatar name={c.contact?.name ?? c.contact?.phone} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                          {c.contact?.name ?? c.contact?.phone ?? 'Contacto'}
                        </p>
                        <p className="truncate text-xs text-slate-400">
                          {c.channel?.name ?? c.channel?.type ?? ''}
                        </p>
                      </div>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      <span className="w-16 shrink-0 text-right text-xs text-slate-400">
                        {relativeTime(c.lastMessageAt ?? c.createdAt)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}
